import {
  FETCH_USERS,
  SET_USERS,
  SET_USERS_FAILURE,
  ADD_USER,
  ADD_USER_SUCCESS,
  ADD_USER_FAILURE,
  UPDATE_USER,
  UPDATE_USER_SUCCESS,
  UPDATE_USER_FAILURE,
  DELETE_USER,
  DELETE_USER_SUCCESS,
  DELETE_USER_FAILURE,
  BULK_ACTION_USERS,
  BULK_ACTION_USERS_SUCCESS,
  BULK_ACTION_USERS_FAILURE,
  RESET_USER_PASSWORD,
  RESET_USER_PASSWORD_SUCCESS,
  RESET_USER_PASSWORD_FAILURE,
  TOGGLE_TWO_FACTOR,
  TOGGLE_TWO_FACTOR_SUCCESS,
  TOGGLE_TWO_FACTOR_FAILURE,
  // User access permissions
  GET_USER_ACCESS,
  SET_USER_ACCESS,
  SET_USER_ACCESS_FAILURE,
  UPDATE_USER_ACCESS,
  UPDATE_USER_ACCESS_SUCCESS,
  UPDATE_USER_ACCESS_FAILURE,
  SAVE_USER_ACCESS,
  SAVE_USER_ACCESS_SUCCESS,
  SAVE_USER_ACCESS_FAILURE,
  RESTORE_USER_ACCESS,
  RESTORE_USER_ACCESS_SUCCESS,
  RESTORE_USER_ACCESS_FAILURE,
  // User permissions after login
  GET_USER_ACCESS_PERMISSIONS,
  SET_USER_ACCESS_PERMISSIONS,
  SET_USER_ACCESS_PERMISSIONS_FAILURE
} from '../types';

const initialState = {
  users: [],
  selectedUsers: [],
  filters: {},
  loading: false,
  error: null,
  addUserLoading: false,
  updateUserLoading: false,
  deleteUserLoading: false,
  bulkActionLoading: false,
  resetPasswordLoading: false,
  toggleTwoFactorLoading: false,
  // User access permissions state
  userAccess: {
    rolePermissions: {},
    loading: false,
    error: null,
    saving: false,
    restoring: false,
    lastSaved: null,
    lastRestored: null
  },
  // User permissions after login state
  userPermissions: {
    data: null,
    loading: false,
    error: null
  }
};

const userReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_USERS:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case SET_USERS: {
      const raw = action.users?.usersData ?? action.users;
      const nextUsers = Array.isArray(raw) ? raw : [];
      return {
        ...state,
        users: nextUsers,
        loading: false,
        error: null
      };
    }
    
    case SET_USERS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error
      };
    
    case ADD_USER:
      return {
        ...state,
        addUserLoading: true,
        error: null
      };
    
    case ADD_USER_SUCCESS:
      return {
        ...state,
        users: [...state.users, action.user],
        addUserLoading: false,
        error: null
      };
    
    case ADD_USER_FAILURE:
      return {
        ...state,
        addUserLoading: false,
        error: action.error
      };
    
    case UPDATE_USER:
      return {
        ...state,
        updateUserLoading: true,
        error: null
      };
    
    case UPDATE_USER_SUCCESS:
      return {
        ...state,
        users: state.users.map(user => 
          user.id === action.user.id ? action.user : user
        ),
        updateUserLoading: false,
        error: null
      };
    
    case UPDATE_USER_FAILURE:
      return {
        ...state,
        updateUserLoading: false,
        error: action.error
      };
    
    case DELETE_USER:
      return {
        ...state,
        deleteUserLoading: true,
        error: null
      };
    
    case DELETE_USER_SUCCESS:
      return {
        ...state,
        users: state.users.filter(user => user.id !== action.userId),
        selectedUsers: state.selectedUsers.filter(id => id !== action.userId),
        deleteUserLoading: false,
        error: null
      };
    
    case DELETE_USER_FAILURE:
      return {
        ...state,
        deleteUserLoading: false,
        error: action.error
      };
    
    case BULK_ACTION_USERS:
      return {
        ...state,
        bulkActionLoading: true,
        error: null
      };
    
    case BULK_ACTION_USERS_SUCCESS:
      return {
        ...state,
        users: action.result.updatedUsers || state.users,
        selectedUsers: [],
        bulkActionLoading: false,
        error: null
      };
    
    case BULK_ACTION_USERS_FAILURE:
      return {
        ...state,
        bulkActionLoading: false,
        error: action.error
      };
    
    case RESET_USER_PASSWORD:
      return {
        ...state,
        resetPasswordLoading: true,
        error: null
      };
    
    case RESET_USER_PASSWORD_SUCCESS:
      return {
        ...state,
        resetPasswordLoading: false,
        error: null
      };
    
    case RESET_USER_PASSWORD_FAILURE:
      return {
        ...state,
        resetPasswordLoading: false,
        error: action.error
      };
    
    case TOGGLE_TWO_FACTOR:
      return {
        ...state,
        toggleTwoFactorLoading: true,
        error: null
      };
    
    case TOGGLE_TWO_FACTOR_SUCCESS:
      return {
        ...state,
        users: state.users.map(user => 
          user.id === action.userId 
            ? { ...user, twoFactorEnabled: action.twoFactorEnabled }
            : user
        ),
        toggleTwoFactorLoading: false,
        error: null
      };
    
    case TOGGLE_TWO_FACTOR_FAILURE:
      return {
        ...state,
        toggleTwoFactorLoading: false,
        error: action.error
      };

    // User Access Permissions Cases
    case GET_USER_ACCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          loading: true,
          error: null
        }
      };

    case SET_USER_ACCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          rolePermissions: action.rolePermissions,
          loading: false,
          error: null
        }
      };

    case SET_USER_ACCESS_FAILURE:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          loading: false,
          error: action.error
        }
      };

    case UPDATE_USER_ACCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          rolePermissions: {
            ...state.userAccess.rolePermissions,
            [action.role]: {
              ...state.userAccess.rolePermissions[action.role],
              [action.permissionId]: action.checked
            }
          }
        }
      };

    case UPDATE_USER_ACCESS_SUCCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          error: null
        }
      };

    case UPDATE_USER_ACCESS_FAILURE:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          error: action.error
        }
      };

    case SAVE_USER_ACCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          saving: true,
          error: null
        }
      };

    case SAVE_USER_ACCESS_SUCCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          saving: false,
          lastSaved: new Date().toISOString(),
          error: null
        }
      };

    case SAVE_USER_ACCESS_FAILURE:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          saving: false,
          error: action.error
        }
      };

    case RESTORE_USER_ACCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          restoring: true,
          error: null
        }
      };

    case RESTORE_USER_ACCESS_SUCCESS:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          rolePermissions: action.rolePermissions,
          restoring: false,
          lastRestored: new Date().toISOString(),
          error: null
        }
      };

    case RESTORE_USER_ACCESS_FAILURE:
      return {
        ...state,
        userAccess: {
          ...state.userAccess,
          restoring: false,
          error: action.error
        }
      };

    // User Permissions After Login Cases
    case GET_USER_ACCESS_PERMISSIONS:
      return {
        ...state,
        userPermissions: {
          ...state.userPermissions,
          loading: true,
          error: null
        }
      };

    case SET_USER_ACCESS_PERMISSIONS:
      return {
        ...state,
        userPermissions: {
          ...state.userPermissions,
          data: action.userPermissions,
          loading: false,
          error: null
        }
      };

    case SET_USER_ACCESS_PERMISSIONS_FAILURE:
      return {
        ...state,
        userPermissions: {
          ...state.userPermissions,
          loading: false,
          error: action.error
        }
      };

    default:
      return state;
  }
};

export default userReducer; 